<template>
    <b-modal
        ref="modalDuplicate"
        lazy
        title-tag="h2"
        title="Copy Template"
        ok-title="Proceed"
        footer-class="d-flex"
        no-close-on-backdrop
        no-close-on-esc
        @shown="modalShown"
    >
        <div v-if="viewLess">
            <div
                v-if="usesItems.length > 0"
                id="dialog-alert-warning"
                class="alert alert-block alert-warning"
                style="padding: 10px;"
            >
                <span id="dialog-alert-message">
                    <i class="cus-error"></i> {{ usesItems.length }} items will
                    not be duplicated because they have specific 'Uses' assigned
                    to them.
                </span>
                <div class="comm-content-wrapper mt-2">
                    <ol id="list-items-uses">
                        <li
                            v-for="usesItem in usesItems.slice(0, 5)"
                            :key="usesItem.encoded_item_id"
                        >
                            <router-link
                                v-if="usesItem.encoded_item_id"
                                target="_blank"
                                class="font-weight-normal"
                                :to="{
                                    name: 'item',
                                    params: { id: usesItem.encoded_item_id }
                                }"
                            >
                                {{ usesItem.item_name }}
                            </router-link>
                            <a v-else class="font-weight-normal text-link">{{
                                usesItem.item_name
                            }}</a>
                        </li>
                    </ol>
                </div>
                <a
                    v-if="usesItems.length > 5"
                    class="uses-link"
                    target="_blank"
                    @click="viewLess = false"
                    >View more</a
                >
            </div>
            <b-form-group label-for="title" label-cols="3" label="Title:">
                <b-input
                    v-model="title"
                    ref="title"
                    name="title"
                    type="text"
                    placeholder="Title (required)"
                    :disabled="isLoading"
                />
            </b-form-group>

            <b-form-group
                label-for="description"
                label-cols="3"
                label="Description:"
            >
                <b-textarea
                    v-model="description"
                    name="description"
                    placeholder="Description (optional)"
                    :disabled="isLoading"
                />
            </b-form-group>
        </div>
        <div v-else>
            <div class="comm-content-wrapper">
                <ol id="list-items-uses">
                    <li
                        v-for="usesItem in usesItems"
                        :key="usesItem.encoded_item_id"
                    >
                        <router-link
                            v-if="usesItem.encoded_item_id"
                            target="_blank"
                            class="font-weight-normal"
                            :to="{
                                name: 'item',
                                params: { id: usesItem.encoded_item_id }
                            }"
                        >
                            {{ usesItem.item_name }}
                        </router-link>
                        <a v-else class="font-weight-normal text-link">{{
                            usesItem.item_name
                        }}</a>
                    </li>
                </ol>
            </div>
        </div>

        <template v-slot:modal-footer="{ close }">
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="viewLess = true"
                v-if="!isLoading && !viewLess"
            >
                Back
            </b-button>

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading && viewLess"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading && viewLess"
                variant="primary"
                @click="proceed"
                :disabled="title === ''"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script type="text/javascript">
import TEMPLATES from "@/constants/templates";
import Loader from "@/module/commons/loader";
import TemplateService from "@/services/templates";

export default {
    components: {
        Loader
    },
    data() {
        return {
            TEMPLATES,
            title: "",
            description: "",
            templateId: null,
            isLoading: false,
            templateType: null,
            usesItems: [],
            viewLess: true,
            isOpenFromTemplateProfile: false
        };
    },
    methods: {
        modalShown() {
            if (this.$refs.title) {
                this.$refs.title.focus();
            }
        },
        async open({
            templateId,
            templateName,
            templateType,
            templateDescription,
            isOpenFromTemplateProfile = false
        }) {
            this.templateId = templateId;
            this.title = "Copy of " + templateName;
            this.templateType = templateType;
            this.description = templateDescription;
            this.isOpenFromTemplateProfile = isOpenFromTemplateProfile;

            if (templateType == TEMPLATES.TYPE.WELLNESS_PLAN) {
                let res = await TemplateService.checkUsesItems(this.templateId);
                if (res.message.code == 200) {
                    this.usesItems = res.items_has_uses;
                } else {
                    this._message("Failed to fetch uses items", "danger");
                }
            }

            this.$refs.modalDuplicate.show();
        },
        async proceed() {
            let mapTypeRouter = {
                1: TEMPLATES.LIST[TEMPLATES.TYPE.BUNDLE].route,
                4: TEMPLATES.LIST[TEMPLATES.TYPE.CONTENT_SNIPPET].route,
                5: TEMPLATES.LIST[TEMPLATES.TYPE.MEDICAL_NOTE].route,
                6: TEMPLATES.LIST[TEMPLATES.TYPE.LETTER].route,
                7: TEMPLATES.LIST[TEMPLATES.TYPE.WELLNESS_PLAN].route,
                10: TEMPLATES.LIST[TEMPLATES.TYPE.PURCHASE_ORDER].route,
                11: TEMPLATES.LIST[TEMPLATES.TYPE.BUNDLE_ESTIMATE].route,
                12: TEMPLATES.LIST[TEMPLATES.TYPE.WEBFORM_TEMPLATE].route
            };

            try {
                let typeRouter = mapTypeRouter[this.templateType];
                let res = null;

                if (!typeRouter) {
                    throw new Error(
                        "Unable to proceed. Invalid template identifier."
                    );
                }

                this.isLoading = true;

                if (this.templateType == TEMPLATES.TYPE.PURCHASE_ORDER) {
                    // purchase-order used old API
                    // need to confirmed with BE if can migrate using barramundi
                    let fd = new FormData();
                    fd.append("id", this.templateId);
                    fd.append("name", this.title);
                    fd.append("description", this.description);

                    res = await this.$http.post(
                        `${process.env.CI_APPS}business/template/save_as_copy`,
                        fd
                    );

                    if (res.data && res.data.id) {
                        // redirect to the new template
                        window.location.href = `/view/settings/template/${typeRouter}/${res.data.id}`;
                    } else {
                        throw new Error(res.data.message);
                    }
                } else {
                    res = await TemplateService.duplicate(this.templateId, {
                        name: this.title,
                        description: this.description,
                        type: this.templateType
                    });

                    if (
                        res.id &&
                        (TEMPLATES.IN_VIEW.includes(this.templateType) ||
                            // this part should be deleted once all setting template migration done
                            this.isOpenFromTemplateProfile)
                    ) {
                        // redirect to the new template
                        this.$refs.modalDuplicate.hide();
                        this.$router.push({
                            name: "template_details",
                            params: { type: typeRouter, id: res.encoded_id }
                        });

                        if (
                            this.templateType == TEMPLATES.TYPE.WEBFORM_TEMPLATE
                        ) {
                            this.$store.commit(
                                "settingsTemplate/setDetails",
                                res
                            );
                        }
                    } else {
                        // redirect to the new template
                        this.$refs.modalDuplicate.hide();
                        window.location =
                            process.env.APPS +
                            "business/template/view/" +
                            res.encoded_id;
                    }
                }

                this.loading = false;
            } catch (error) {
                this.isLoading = false;
                this._message(error.message, "danger");
            }
        }
    }
};
</script>
<style scoped>
.uses-link {
    color: #0088cc !important;
    font-weight: normal;
    cursor: pointer;
}
</style>
